<template>
  <!-- Nothing pending, nothing shown. A control that is permanently visible and
       permanently reads zero is the one people stop looking at. -->
  <v-menu
    v-if="pendingCount > 0"
    v-model="open"
    location="bottom end"
    offset="8"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        variant="text"
        size="default"
        class="mr-1 pending-doses-btn"
        :aria-label="t('quickEntry.pending.a11y', { count: pendingCount })"
      >
        <v-badge :content="pendingCount" color="warning" offset-x="-2" offset-y="-2">
          <v-icon icon="fa-regular fa-clipboard-medical" size="18" aria-hidden="true" />
        </v-badge>
      </v-btn>
    </template>

    <v-card min-width="320" max-width="400" elevation="8" rounded="lg">
      <v-card-text class="px-4 py-3 border-b">
        <div class="text-body-1 font-weight-medium text-grey-darken-3">
          {{ t('quickEntry.pending.title') }}
        </div>
        <div class="text-body-2 text-grey-darken-1">
          {{ t('quickEntry.pending.subtitle', { days: PENDING_WINDOW_DAYS }) }}
        </div>
      </v-card-text>

      <v-list density="compact" class="py-1" max-height="360" style="overflow-y: auto;">
        <v-list-item
          v-for="entry in pendingResults"
          :key="entry.id"
          class="pending-doses-item"
          @click="openResult(entry)"
        >
          <template v-slot:prepend>
            <PatientAvatar :name="entry.patientName" :id="entry.patientId" :size="32" class="mr-3" />
          </template>
          <template v-slot:title>
            <span class="font-weight-medium text-body-2">{{ entry.patientName }}</span>
          </template>
          <template v-slot:subtitle>
            <span class="text-caption text-grey-darken-1 pending-doses-meta">
              {{ formatValue(entry.resultValue) }} · {{ formatDate(entry.resultDate) }}
            </span>
          </template>
          <template v-slot:append>
            <v-icon icon="fa-solid fa-chevron-right" size="12" class="text-grey" aria-hidden="true" />
          </template>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PatientAvatar from './PatientAvatar.vue';
import { usePendingDoses, PENDING_WINDOW_DAYS } from '../composables/usePendingDoses.js';

defineOptions({ name: 'pending-doses-button' });

const { t, locale } = useI18n();
const router = useRouter();
const { pendingResults, pendingCount } = usePendingDoses();

const open = ref(false);

function formatValue(value) {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat(locale.value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(num);
}

function formatDate(value) {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return String(value);
  return new Intl.DateTimeFormat(locale.value, { day: 'numeric', month: 'short' })
    .format(new Date(year, month - 1, day));
}

/**
 * The dose editor is addressed by patient in the path and by result in the
 * query — the same contract RecurrentDosesRoute already reads.
 */
function openResult(entry) {
  open.value = false;
  router.push({
    name: 'recurrentDoses',
    params: { id: String(entry.patientId) },
    query: { resultId: String(entry.id) },
  });
}
</script>

<style scoped>
.pending-doses-btn {
  min-width: 42px;
}

.pending-doses-item {
  cursor: pointer;
  border-radius: 8px;
  margin: 0 8px 4px;
}

.pending-doses-item:hover {
  background-color: rgba(37, 99, 235, 0.06);
}

.pending-doses-meta {
  font-variant-numeric: tabular-nums;
}
</style>
